import type { FC } from 'react';
import React, { useRef, useState } from 'react';
import { Box } from '@withjoy/joykit';
import { animationTransition } from '@shared/utils/animationTransition';
import { useSkipInitialAnimation } from '@apps/guest/packages/layout-engine/hooks/useSkipInitialAnimation';
import { useDrawCroppedImageToCanvas } from '@shared/hooks/useDrawCroppedImageToCanvas';
import { useProgressiveImage } from './useProgressiveImage';
import { AnimatedPhotoBox } from './OptimizedEventGuestSitePhoto.styles';
import type { OptimizedEventGuestSitePhotoProps } from './OptimizedEventGuestSitePhoto.types';

// Small delay so the high-res image has time to decode/paint before the low-res
// starts fading out. Without this, the low-res opacity transition begins on the
// same frame the high-res src is set, causing a brief flicker.
const CROSSFADE_TRANSITION = animationTransition('opacity', { delay: '150ms' });
// When the high-res is served from disk cache, skip the transition entirely.
const INSTANT_TRANSITION = 'opacity 0s';

// Blur effect on the low-res (tiny) layer to mimic blur-hash behavior.
// The tiny rendition is very low resolution — blurring it creates a smooth
// placeholder that sharpens when the high-res layer fades in.
// scale(1.05) extends the blurred layer slightly beyond the container to hide
// the transparent edge artifacts that CSS blur creates at boundaries.
// 5% is enough to cover the 20px blur radius on typical container sizes
// without causing noticeable layout shift during the crossfade.
const LOW_RES_BLUR_FILTER = 'blur(20px)';
const LOW_RES_BLUR_SCALE = 'scale(1.05)';

// ---------------------------------------------------------------------------
// Cropped: fixed-aspect containers (PhotoTrio, PhotoDuo, PhotoArch, etc.)
//
// Uses an <img> element inside an overflow:hidden container. The crop region
// is replicated via CSS transforms derived from the PercentCrop values:
//
//   width:  10000 / cropWidth  %   → scales the image so the crop region fills the container width
//   height: 10000 / cropHeight %   → same for height
//   left:  -(x / cropWidth) * 100% → shifts the image left so the crop region starts at x=0
//   top:   -(y / cropHeight) * 100% → same for vertical offset
//
// Both dimensions use percentages relative to the container, so the crop
// renders correctly at any container size without JS measurement.
// ---------------------------------------------------------------------------
const CroppedFixedAspect: FC<{
  lowResUrl: string;
  highResUrl: string;
  isHighResLoaded: boolean;
  isHighResFromCache: boolean;
  cropInfo: NonNullable<OptimizedEventGuestSitePhotoProps['cropInfo']>;
  onHighResError: () => void;
}> = ({ lowResUrl, highResUrl, isHighResLoaded, isHighResFromCache, cropInfo, onHighResError }) => {
  const { x, y, width: cw, height: ch } = cropInfo;
  const imgStyle = {
    width: `${10000 / cw}%`,
    height: `${10000 / ch}%`,
    left: `${-(x / cw) * 100}%`,
    top: `${-(y / ch) * 100}%`,
  };

  return (
    <Box
      width="100%"
      height="100%"
      overflow="hidden"
      position="relative"
    >
      {/* High-res layer — always in DOM so it's pre-mounted and ready to paint.
          src is only set when useProgressiveImage reports the image is cached,
          preventing the browser from eagerly fetching xlarge in parallel with small. */}
      <Box
        as="img"
        src={isHighResLoaded ? highResUrl : undefined}
        alt=""
        position="absolute"
        zIndex={1}
        onError={onHighResError}
        style={imgStyle}
      />
      {/* Low-res layer — stacks above high-res (z-index 2 > 1), fades out to
          reveal the sharp high-res underneath once it's loaded and painted. */}
      <Box
        as="img"
        src={lowResUrl}
        alt=""
        position="absolute"
        zIndex={2}
        style={{
          ...imgStyle,
          filter: LOW_RES_BLUR_FILTER,
          transform: LOW_RES_BLUR_SCALE,
          opacity: isHighResLoaded ? 0 : 1,
          transition: isHighResFromCache ? INSTANT_TRANSITION : CROSSFADE_TRANSITION,
        }}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Cropped: square containers (Aloha slider)
//
// Same <img> crop math as CroppedFixedAspect, but wrapped in a square
// intermediate div (aspect-ratio: 1) centered via translate(-50%, -50%).
// The square container ensures percentage-based crop transforms resolve
// equally in both dimensions — without it, a rectangular container would
// stretch the crop positioning unevenly.
// ---------------------------------------------------------------------------
const CroppedSquare: FC<{
  lowResUrl: string;
  highResUrl: string;
  isHighResLoaded: boolean;
  isHighResFromCache: boolean;
  cropInfo: NonNullable<OptimizedEventGuestSitePhotoProps['cropInfo']>;
  onHighResError: () => void;
}> = ({ lowResUrl, highResUrl, isHighResLoaded, isHighResFromCache, cropInfo, onHighResError }) => {
  const { x, y, width: cw, height: ch } = cropInfo;
  const imgStyle = {
    width: `${10000 / cw}%`,
    height: `${10000 / ch}%`,
    left: `${-(x / cw) * 100}%`,
    top: `${-(y / ch) * 100}%`,
  };

  return (
    <Box
      width="100%"
      height="100%"
      overflow="hidden"
      position="relative"
    >
      {/* Square intermediate div — min-width/min-height: 100% ensures it covers
          the container regardless of aspect ratio. Centered with translate. */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        minWidth="100%"
        minHeight="100%"
        overflow="hidden"
        style={{ aspectRatio: '1', transform: 'translate(-50%, -50%)' }}
      >
        <Box
          as="img"
          src={isHighResLoaded ? highResUrl : undefined}
          alt=""
          position="absolute"
          zIndex={1}
          onError={onHighResError}
          style={imgStyle}
        />
        <Box
          as="img"
          src={lowResUrl}
          alt=""
          position="absolute"
          zIndex={2}
          style={{
            ...imgStyle,
            filter: LOW_RES_BLUR_FILTER,
            transform: LOW_RES_BLUR_SCALE,
            opacity: isHighResLoaded ? 0 : 1,
            transition: isHighResFromCache ? INSTANT_TRANSITION : CROSSFADE_TRANSITION,
          }}
        />
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Cropped: viewport containers (PhotoBackground)
//
// Uses background-image with background-size: cover instead of <img>.
// An intermediate div is sized by the container's aspect ratio (not the
// crop's) with min-width/min-height: 100% — this replicates the canvas
// "bestSuitable" sizing where the image always covers the viewport.
//
// background-position centers on the crop region:
//   x + cropWidth/2  → horizontal center of the crop as a percentage
//   y + cropHeight/2 → vertical center of the crop as a percentage
//
// At wide viewports the intermediate div grows wider → the image "blooms"
// (zooms in) rather than revealing more of the photo outside the crop.
// ---------------------------------------------------------------------------
const CroppedViewport: FC<{
  lowResUrl: string;
  highResUrl: string;
  isHighResLoaded: boolean;
  isHighResFromCache: boolean;
  cropInfo: NonNullable<OptimizedEventGuestSitePhotoProps['cropInfo']>;
  containerAspectRatio: number;
}> = ({ lowResUrl, highResUrl, isHighResLoaded, isHighResFromCache, cropInfo, containerAspectRatio }) => {
  const { x, y, width: cw, height: ch } = cropInfo;
  const bgPosition = `${x + cw / 2}% ${y + ch / 2}%`;

  return (
    <Box
      width="100%"
      height="100%"
      overflow="hidden"
      position="relative"
    >
      <Box
        position="absolute"
        top="50%"
        left="50%"
        minWidth="100%"
        minHeight="100%"
        style={{
          aspectRatio: `${containerAspectRatio}`,
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={1}
          style={{
            backgroundImage: isHighResLoaded ? `url("${highResUrl}")` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: bgPosition,
            backgroundRepeat: 'no-repeat',
          }}
        />
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={2}
          style={{
            backgroundImage: `url("${lowResUrl}")`,
            backgroundSize: 'cover',
            backgroundPosition: bgPosition,
            backgroundRepeat: 'no-repeat',
            filter: LOW_RES_BLUR_FILTER,
            transform: LOW_RES_BLUR_SCALE,
            opacity: isHighResLoaded ? 0 : 1,
            transition: isHighResFromCache ? INSTANT_TRANSITION : CROSSFADE_TRANSITION,
          }}
        />
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Uncropped: all container types
//
// Two stacked background-image layers with background-size: cover.
// No crop math needed — the browser handles positioning via cover + center.
// ---------------------------------------------------------------------------
const UncroppedPhoto: FC<{
  lowResUrl: string;
  highResUrl: string;
  isHighResLoaded: boolean;
  isHighResFromCache: boolean;
  backgroundPosition?: string;
}> = ({ lowResUrl, highResUrl, isHighResLoaded, isHighResFromCache, backgroundPosition = 'center' }) => (
  <Box
    width="100%"
    height="100%"
    position="relative"
  >
    <Box
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex={1}
      style={{
        backgroundImage: isHighResLoaded ? `url("${highResUrl}")` : undefined,
        backgroundSize: 'cover',
        backgroundPosition,
        backgroundRepeat: 'no-repeat',
      }}
    />
    <Box
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex={2}
      style={{
        backgroundImage: lowResUrl ? `url("${lowResUrl}")` : undefined,
        backgroundColor: 'lightgray',
        backgroundSize: 'cover',
        backgroundPosition,
        backgroundRepeat: 'no-repeat',
        filter: LOW_RES_BLUR_FILTER,
        opacity: isHighResLoaded ? 0 : 1,
        transition: isHighResFromCache ? INSTANT_TRANSITION : CROSSFADE_TRANSITION,
      }}
    />
  </Box>
);

// ---------------------------------------------------------------------------
// Canvas fallback — used when CSS <img> crop fails (onError). Falls back to
// useDrawCroppedImageToCanvas which draws the cropped region to a <canvas>.
// This is a safety net, not the primary rendering path.
// ---------------------------------------------------------------------------
const CanvasFallback: FC<{
  imageUrl: string;
  cropInfo: NonNullable<OptimizedEventGuestSitePhotoProps['cropInfo']>;
  width: number;
  height: number;
}> = ({ imageUrl, cropInfo, width, height }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useDrawCroppedImageToCanvas({ canvasRef, cropInfo, height, width, imageUrl });

  return (
    <Box
      width={width}
      height={height}
      position="relative"
    >
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component
//
// Orchestrates progressive loading, animation, and rendering strategy.
// Internally calls useSkipInitialAnimation (SSR fade-in skip) and
// useProgressiveImage (small → xlarge crossfade), so consumers don't need to.
//
// `isolation: isolate` creates a stacking context so the internal z-indexes
// (used for the two-layer crossfade) don't leak into the parent and interfere
// with sibling elements like decorative overlays (e.g. ArchBorderOverlay).
// ---------------------------------------------------------------------------
export const OptimizedEventGuestSitePhoto: FC<OptimizedEventGuestSitePhotoProps> = props => {
  const { photoUrl, cropInfo, width, height, containerType, containerAspectRatio, lowResRendition, highResRendition, backgroundPosition } = props;

  const skipAnimation = useSkipInitialAnimation();
  const { lowResUrl, highResUrl, isHighResLoaded, isHighResFromCache } = useProgressiveImage({
    photoUrl,
    lowResRendition,
    highResRendition,
  });

  const [useCanvasFallback, setUseCanvasFallback] = useState(false);

  const handleHighResError = () => {
    setUseCanvasFallback(true);
  };

  // Canvas fallback for cropped fixed-aspect/square when CSS <img> fails
  if (useCanvasFallback && cropInfo) {
    return (
      <AnimatedPhotoBox
        skipAnimation={skipAnimation}
        width="100%"
        height="100%"
        style={{ isolation: 'isolate' }}
      >
        <CanvasFallback
          imageUrl={isHighResLoaded ? highResUrl : lowResUrl}
          cropInfo={cropInfo}
          width={width}
          height={height}
        />
      </AnimatedPhotoBox>
    );
  }

  const renderPhoto = () => {
    if (!cropInfo) {
      return (
        <UncroppedPhoto
          lowResUrl={lowResUrl}
          highResUrl={highResUrl}
          isHighResLoaded={isHighResLoaded}
          isHighResFromCache={isHighResFromCache}
          backgroundPosition={backgroundPosition}
        />
      );
    }

    switch (containerType) {
      case 'viewport':
        return (
          <CroppedViewport
            lowResUrl={lowResUrl}
            highResUrl={highResUrl}
            isHighResLoaded={isHighResLoaded}
            isHighResFromCache={isHighResFromCache}
            cropInfo={cropInfo}
            containerAspectRatio={containerAspectRatio!}
          />
        );
      case 'square':
        return (
          <CroppedSquare
            lowResUrl={lowResUrl}
            highResUrl={highResUrl}
            isHighResLoaded={isHighResLoaded}
            isHighResFromCache={isHighResFromCache}
            cropInfo={cropInfo}
            onHighResError={handleHighResError}
          />
        );
      case 'fixed-aspect':
      default:
        return (
          <CroppedFixedAspect
            lowResUrl={lowResUrl}
            highResUrl={highResUrl}
            isHighResLoaded={isHighResLoaded}
            isHighResFromCache={isHighResFromCache}
            cropInfo={cropInfo}
            onHighResError={handleHighResError}
          />
        );
    }
  };

  return (
    <AnimatedPhotoBox
      skipAnimation={skipAnimation}
      width="100%"
      height="100%"
      style={{ isolation: 'isolate' }}
    >
      {renderPhoto()}
    </AnimatedPhotoBox>
  );
};

OptimizedEventGuestSitePhoto.displayName = 'OptimizedEventGuestSitePhoto';
